import { TZDateMini } from '@date-fns/tz';
import {
  addDays as dateFnsAddDays,
  differenceInCalendarYears as dateFnsDifferenceInCalendarYears,
  format as dateFnsFormat,
  formatDistanceToNowStrict as dateFnsFormatDistanceToNow,
  formatDuration as dateFnsFormatDuration,
  getMonth as dateFnsGetMonth,
  getYear as dateFnsGetYear,
  isAfter as dateFnsIsAfter,
  isBefore as dateFnsIsBefore,
  isTomorrow as dateFnsIsTomorrow,
  sub as dateFnsSub,
} from 'date-fns';
import { enGB, es } from 'date-fns/locale';
import i18n, { DEFAULT_LNG } from 'shared/i18n';

const locales = {
  en: enGB,
  es,
};

function addDays({ amount, date }) {
  return dateFnsAddDays(parseWithTz({ date }), amount);
}

function asFormattedDateTime({ date }) {
  return formatDate({
    date,
    format: `dd MMM yy '${i18n.t('shared.prepositions.at', { ns: 'common' })}' HH:mm`,
  });
}

function differenceInCalendarYears({ earlierDate, laterDate }) {
  return dateFnsDifferenceInCalendarYears(
    parseWithTz({ date: laterDate }),
    parseWithTz({ date: earlierDate }),
  );
}

function formatDate({ date, format }) {
  return dateFnsFormat(parseWithTz({ date }), format, {
    locale: locales[i18n.language],
  });
}

function formatDistanceToNow({ date }) {
  return dateFnsFormatDistanceToNow(parseWithTz({ date }), {
    addSuffix: true,
    locale: locales[i18n.language],
  });
}

function formatDuration({ durationParts }) {
  return dateFnsFormatDuration(durationParts, {
    locale: locales[i18n.language],
  });
}

function getMonthIndex({ date }) {
  return dateFnsGetMonth(parseWithTz({ date }));
}

function getMonthName({ monthIndex, width }) {
  return locales[i18n.language || DEFAULT_LNG].localize.month(monthIndex, {
    width,
  });
}

function getYear({ date }) {
  return dateFnsGetYear(parseWithTz({ date }));
}

function isAfter({ date, dateToCompare }) {
  return dateFnsIsAfter(
    parseWithTz({ date }),
    parseWithTz({ date: dateToCompare }),
  );
}

function isBefore({ date, dateToCompare }) {
  return dateFnsIsBefore(
    parseWithTz({ date }),
    parseWithTz({ date: dateToCompare }),
  );
}

function isTomorrow({ date }) {
  return dateFnsIsTomorrow(parseWithTz({ date }));
}

function parseWithTz({ date }) {
  if (Object.prototype.toString.call(date) === '[object Date]') return date;

  const offset =
    typeof date === 'string' ?
      date.match(/([-+])(\d{2}):(\d{2})$/)?.at(0)
    : null;

  return new TZDateMini(date, offset);
}

function sub({ date, durationParts }) {
  return dateFnsSub(parseWithTz({ date }), durationParts);
}

export {
  addDays,
  asFormattedDateTime,
  differenceInCalendarYears,
  formatDate,
  formatDistanceToNow,
  formatDuration,
  getMonthIndex,
  getMonthName,
  getYear,
  isAfter,
  isBefore,
  isTomorrow,
  locales,
  sub,
};
